import { Check, ChevronsUpDown } from "lucide-react";
import { useGetList, useGetOne, useTranslate } from "ra-core";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

import type { Contact } from "../../types";
import { getFullName } from "../cardModel";
import { FREE_TASKS } from "../filters";

const SEARCH_RESULTS = 20;

export interface CardFilterProps {
  /** A contact id, `FREE_TASKS`, or null for any card. */
  value: string | null;
  onChange: (value: string | null) => void;
}

/** Filters tasks by card, searching the contacts on the server. */
export const CardFilter = ({ value, onChange }: CardFilterProps) => {
  const translate = useTranslate();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const isContact = !!value && value !== FREE_TASKS;

  const { data: contacts = [] } = useGetList<Contact>(
    "contacts_summary",
    {
      pagination: { page: 1, perPage: SEARCH_RESULTS },
      sort: { field: "last_seen", order: "DESC" },
      filter: search.trim() ? { q: search.trim() } : {},
    },
    { enabled: open },
  );
  const { data: selectedContact } = useGetOne<Contact>(
    "contacts",
    { id: value ?? "" },
    { enabled: isContact },
  );

  const label = translate("crm.operations.filters.card");
  const currentLabel = !value
    ? `${label}: ${translate("crm.operations.filters.any")}`
    : value === FREE_TASKS
      ? translate("crm.operations.tasks.free")
      : selectedContact
        ? getFullName(selectedContact)
        : "…";

  const select = (next: string | null) => {
    onChange(next);
    setOpen(false);
    setSearch("");
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn(
            "h-8 justify-between gap-2 font-normal",
            value && "border-primary",
          )}
          aria-label={label}
        >
          <span className="max-w-40 truncate">{currentLabel}</span>
          <ChevronsUpDown className="opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            value={search}
            onValueChange={setSearch}
            placeholder={translate("crm.operations.filters.search")}
          />
          <CommandList>
            <CommandGroup>
              <CommandItem value="__any__" onSelect={() => select(null)}>
                <Check className={!value ? "opacity-100" : "opacity-0"} />
                {translate("crm.operations.filters.any")}
              </CommandItem>
              <CommandItem
                value={FREE_TASKS}
                onSelect={() => select(FREE_TASKS)}
              >
                <Check
                  className={value === FREE_TASKS ? "opacity-100" : "opacity-0"}
                />
                {translate("crm.operations.tasks.free")}
              </CommandItem>
              {contacts.map((contact) => (
                <CommandItem
                  key={contact.id}
                  value={String(contact.id)}
                  onSelect={() => select(String(contact.id))}
                >
                  <Check
                    className={
                      value === String(contact.id) ? "opacity-100" : "opacity-0"
                    }
                  />
                  <span className="truncate">{getFullName(contact)}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
